import React, { useState, useEffect } from "react";
import Background from './components/Background/Background';

// Demo email content
const DEMO_EMAIL = `Subject: Design Brief for Modern Family Home Extension & Renovation

Hi there,

We're excited to start planning a comprehensive renovation of our home in Hampstead, London. We purchased this 1920s Arts & Crafts semi-detached house 18 months ago and are now ready to transform it into our forever home for our family of four (two young children, ages 5 and 8).

PROJECT SCOPE:

Rear Extension:
We'd like to extend approximately 6 meters into our garden to create an open-plan kitchen, dining, and family living space. The total area would be roughly 45 square meters. We're envisioning floor-to-ceiling sliding glass doors (ideally Crittall-style or similar) that open onto a raised deck and landscaped garden. Natural light is absolutely crucial—we'd love to explore roof lights or a glazed roof section.

Side Return:
There's a narrow side passage (about 1.2m wide) that we'd like to incorporate into the ground floor layout to maximize space. This could house a utility room, WC, or additional storage.

Loft Conversion:
Convert the existing loft into a master suite with an ensuite bathroom and walk-in wardrobe. We'll need dormer windows on the rear elevation for headroom and light. The room should feel like a peaceful retreat—somewhere to escape from family life!

Ground Floor Reconfiguration:
Currently, the front reception room, middle room, and kitchen are all separated by walls. We'd like to assess what's structural and explore opening up the layout for better flow while maintaining distinct zones for different activities.

First Floor:
Reconfigure the existing bathroom and possibly adjust bedroom layouts to better suit our children's needs as they grow. We'd also like to add a second WC if feasible.

DESIGN STYLE & PREFERENCES:

We're drawn to contemporary Scandinavian design with clean lines, natural materials, and a warm, minimalist aesthetic. Think light oak floors, white walls, brass fixtures, and lots of plants. We want to respect the original character of the house (preserve the front bay windows, original fireplaces, and ceiling roses) while creating a striking modern contrast in the extension.

Key influences: Dezeen, Grand Designs episodes, Japanese minimalism meets hygge.

SUSTAINABILITY & PERFORMANCE:

This is really important to us. We want to future-proof the house with:
- Solar panels (roof has good south-facing aspect)
- Air source heat pump or similar renewable heating
- High-performance insulation and triple glazing in new elements
- Underfloor heating throughout the extension
- Rainwater harvesting system for garden irrigation
- EV charging point in the driveway
- Natural ventilation strategies to minimize mechanical cooling

FUNCTIONAL REQUIREMENTS:

Kitchen: Large island with seating for 4-5, built-in appliances (double oven, induction hob, integrated fridge/freezer), butler's pantry or separate prep zone, tons of storage
Dining: Space for 8-person table
Living: Cozy seating area with potential for wood-burning stove or bio-ethanol fireplace
Storage: We need WAY more storage—coats, shoes, sports equipment, children's toys
Work from home: A small desk nook or dedicated office space would be ideal
Bike storage: Secure, covered space for 4 bikes

CONSTRAINTS & CONSIDERATIONS:

- Property is in a conservation area (not listed, but overlooked by listed buildings)
- Neighbors are generally supportive but we want to be considerate
- Party wall agreements will be needed on both sides
- Existing garden has a mature oak tree with a TPO that we must work around
- We have access to the rear garden from the side passage

BUDGET & TIMELINE:

Budget: £250,000-280,000 (we know this is ambitious but we're being realistic about priorities)
We have some flexibility and might phase the project if needed. Loft conversion could potentially be Phase 2.

Timeline: 
- Design and planning approval: Next 6-9 months
- Start construction: Autumn 2025
- Completion: Summer 2026
- We'll stay with family during major groundworks but want to remain in the house as much as possible

We'd love to start with a feasibility study and initial concepts to understand what's possible within our budget. Happy to provide measured plans, photographs, and any other information you need.

Looking forward to collaborating on this!

Best regards,
Emma & David Richardson
07XXX XXXXXX
emma.richardson@email.com`;

const DEMO_PDF_URL = '/demo-assets/Hampstead%20Design%20Brief.pdf';
const DEMO_PDF_FILENAME = 'Hampstead Design Brief.pdf';

// Main Demo App Component
export default function DemoApp() {
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfFilename, setPdfFilename] = useState(DEMO_PDF_FILENAME);
  const [, setShowEmailWindow] = useState(true);
  const [currentStep, setCurrentStep] = useState(1); // 1: copy, 2: paste, 3: complete
  const [emailCopied, setEmailCopied] = useState(false);
  
  const [modalState, setModalState] = useState('closed');
  const [buttonRect, setButtonRect] = useState(null);
  const [loadingStage, setLoadingStage] = useState(0);

  useEffect(() => {
    if (buttonRect) {
      setModalState('preparing');
    }
  }, [buttonRect]);

  useEffect(() => {
    if (modalState === 'closed') {
      setButtonRect(null);
    }
  }, [modalState]);

  // No auto-advance based on paste - user explicitly moves forward by copying

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(DEMO_EMAIL);
    setEmailCopied(true);
    // Auto-advance to step 2 after showing success feedback
    setTimeout(() => {
      setShowEmailWindow(false);
      // Small delay before advancing step for smooth transition
      setTimeout(() => {
        setCurrentStep(2);
      }, 300);
    }, 1200);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!notes.trim()) {
      return;
    }
    
    setLoading(true);
    setCurrentStep(3);
    setLoadingStage(0);
    
    // Minimum time to show all loading stages (4 seconds to complete all 3 stages)
    const minimumLoadingTime = 4000;
    const loadingStartTime = Date.now();
    
    // Progress through stages
    const stageTimers = [
      setTimeout(() => setLoadingStage(1), 800),   // Reading content
      setTimeout(() => setLoadingStage(2), 2000),  // Analyzing structure
      setTimeout(() => setLoadingStage(3), 3200),  // Formatting brief
    ];
    
    try {
      const elapsedTime = Date.now() - loadingStartTime;
      const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
      await new Promise(resolve => setTimeout(resolve, remainingTime));

      setPdfUrl(DEMO_PDF_URL);
      setPdfFilename(DEMO_PDF_FILENAME);
      setCurrentStep(3);
    } finally {
      // Clear all stage timers
      stageTimers.forEach(timer => clearTimeout(timer));
      setLoading(false);
      setLoadingStage(0);
    }
  };

  const downloadPdf = () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = pdfFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Background />
      <div className="min-h-screen flex flex-col relative">
        {/* Demo Banner */}
        <div className="gradient-hero text-white py-3 px-4 text-center font-semibold shadow-strong z-20 sticky top-0">
          <span className="text-lg font-bold">Interactive Demo</span>
          <span className="mx-3 opacity-70">|</span>
          <span className="text-sm opacity-90">Try it out with sample data - No signup required</span>
        </div>

        {/* Header */}
        <header className="backdrop-blur-sm border-b border-white/20 sticky top-12 z-10 shadow-sm">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-center">
            <h1 className="text-2xl font-extrabold text-white tracking-tight drop-shadow-lg">Archibrief</h1>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex flex-col items-center justify-center px-4 py-6 md:py-12 relative min-h-[600px]">
          {/* Step Indicator - Always visible */}
          <div className="w-full max-w-2xl mb-6 md:mb-8">
            <div className="flex items-center justify-between">
              {[
                { num: 1, label: 'Copy Email', active: currentStep === 1 },
                { num: 2, label: 'Paste & Generate', active: currentStep === 2 },
                { num: 3, label: 'View Result', active: currentStep === 3 }
              ].map((step, idx) => (
                <React.Fragment key={step.num}>
                  <button
                    onClick={() => {
                      // Allow going back to previous steps with proper reset
                      if (step.num < currentStep) {
                        if (step.num === 1) {
                          // Reset step 1: show email window and reset copied state
                          setShowEmailWindow(true);
                          setEmailCopied(false);
                          setCurrentStep(1);
                        }
                        if (step.num === 2) {
                          // Reset step 2: clear notes and PDF
                          setNotes('');
                          setPdfUrl('');
                          setCurrentStep(2);
                        }
                      }
                    }}
                    disabled={step.num >= currentStep}
                    className="flex flex-col items-center group"
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                      step.active 
                        ? 'text-white shadow-lg scale-110' 
                        : currentStep > step.num
                        ? 'text-white cursor-pointer group-hover:scale-105'
                        : 'bg-white/60 text-gray-500 cursor-default'
                    }`} 
                    style={
                      step.active || currentStep > step.num
                        ? { background: 'linear-gradient(135deg, hsl(28, 80%, 58%), hsl(28, 85%, 65%))' }
                        : {}
                    }>
                      {currentStep > step.num ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : step.num}
                    </div>
                    <span className={`text-xs mt-1 font-medium transition-all ${
                      step.active 
                        ? 'text-white' 
                        : currentStep > step.num
                        ? 'text-white/60 group-hover:text-white/80'
                        : 'text-white/60'
                    }`}>{step.label}</span>
                  </button>
                  {idx < 2 && (
                    <div className={`flex-1 h-1 mx-2 rounded transition-all`} style={
                      currentStep > step.num 
                        ? { background: 'linear-gradient(135deg, hsl(28, 80%, 58%), hsl(28, 85%, 65%))' }
                        : { background: 'rgba(255, 255, 255, 0.3)' }
                    } />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Step 1: Email Window */}
          {currentStep === 1 && (
            <div className="w-full max-w-2xl mb-6 animate-slideDownSmooth">
              <div className="bg-white rounded-xl overflow-hidden shadow-strong border border-gray-100">
                {/* Email Header */}
                <div className="gradient-hero px-4 py-3 border-b border-white/20 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'hsl(208, 50%, 85%)' }}>
                      <svg className="w-5 h-5" style={{ color: 'hsl(208, 61%, 50%)' }} fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
                      </svg>
                    </div>
                    <span className="text-sm font-semibold text-white">Sample Client Email</span>
                  </div>
                </div>

                {/* Email Metadata */}
                <div className="px-6 py-4 bg-white border-b border-gray-100">
                  <div className="text-sm space-y-2">
                    <div className="flex items-start gap-2">
                      <span className="font-semibold" style={{ color: 'hsl(210, 11%, 20%)' }}>From:</span>
                      <span style={{ color: 'hsl(210, 6%, 56%)' }}>Emma Richardson &lt;emma.richardson@email.com&gt;</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="font-semibold" style={{ color: 'hsl(210, 11%, 20%)' }}>Subject:</span>
                      <span style={{ color: 'hsl(210, 6%, 56%)' }}>Design Brief for Modern Family Home Extension & Renovation</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold" style={{ color: 'hsl(210, 11%, 20%)' }}>Date:</span>
                      <span style={{ color: 'hsl(210, 6%, 56%)' }}>Today, 9:23 AM</span>
                    </div>
                  </div>
                </div>

                {/* Email Body */}
                <div className="px-6 py-6 bg-white max-h-96 overflow-y-auto">
                  <div className="whitespace-pre-wrap leading-relaxed text-sm" style={{ color: 'hsl(210, 11%, 20%)' }}>
                    {DEMO_EMAIL}
                  </div>
                </div>

                {/* Email Actions */}
                <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between" style={{ backgroundColor: 'hsl(210, 11%, 96%)' }}>
                  <div className="text-sm" style={{ color: 'hsl(210, 6%, 56%)' }}>
                    {emailCopied ? (
                      <span className="font-semibold flex items-center gap-1" style={{ color: 'hsl(142, 71%, 45%)' }}>
                        Copied to clipboard!
                      </span>
                    ) : (
                      <span>Copy this email and paste it below</span>
                    )}
                  </div>
                  <button
                    onClick={handleCopyEmail}
                    disabled={emailCopied}
                    className="px-6 py-3 md:px-4 md:py-2 text-white rounded-lg font-semibold transition-all duration-300 shadow-glow flex items-center gap-2 transform hover:scale-105 active:scale-95 disabled:scale-100 min-h-[44px]"
                    style={{ 
                      background: emailCopied 
                        ? 'linear-gradient(135deg, hsl(142, 71%, 45%), hsl(142, 71%, 50%))' 
                        : 'hsl(var(--accent))',
                      backgroundColor: emailCopied ? '' : 'hsl(28, 80%, 58%)'
                    }}
                  >
                    {emailCopied ? (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        Copied!
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy Email
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Generate Form */}
          {currentStep === 2 && (
            <>
              <div className="w-full max-w-2xl mb-6 animate-slideDownSmooth">
                <div className="bg-white rounded-xl p-6 shadow-soft border" style={{ borderColor: 'hsl(210, 11%, 90%)' }}>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-2xl gradient-accent flex items-center justify-center shadow-glow">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-lg mb-1" style={{ color: 'hsl(210, 11%, 20%)' }}>Email copied!</p>
                      <p className="text-base" style={{ color: 'hsl(210, 6%, 56%)' }}>Paste the email below and click "Generate Brief" to see AI in action</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Main Form */}
              <form
                onSubmit={handleSubmit}
                className="w-full max-w-2xl bg-white/80 backdrop-blur-sm rounded-2xl border p-6 md:p-8 flex flex-col gap-6 animate-slideDownSmooth shadow-soft"
                style={{ borderColor: 'hsl(210, 11%, 90%)' }}
              >
            <label htmlFor="notes" className="block text-lg font-semibold mb-2" style={{ color: 'hsl(210, 11%, 20%)' }}>
              Paste Client Notes, Emails or Messages:
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={7}
              className="w-full rounded-lg border p-4 text-base focus:outline-none focus:ring-2 transition resize-vertical"
              style={{
                borderColor: 'hsl(210, 11%, 90%)',
                backgroundColor: 'hsl(210, 11%, 96%)',
                color: 'hsl(210, 11%, 20%)'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'hsl(208, 61%, 50%)';
                e.target.style.boxShadow = '0 0 0 3px hsl(208, 50%, 85%)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'hsl(210, 11%, 90%)';
                e.target.style.boxShadow = 'none';
              }}
              placeholder="Paste the sample email here..."
            />
            
            {/* Word counter */}
            {notes.trim() && (
              <div className="p-3 rounded-lg border" style={{
                backgroundColor: 'hsl(210, 11%, 96%)',
                borderColor: 'hsl(210, 11%, 90%)'
              }}>
                <div className="flex items-center justify-between">
                  <div className="text-sm" style={{ color: 'hsl(210, 6%, 56%)' }}>
                    <span>Word count: <span className="font-semibold" style={{ color: 'hsl(210, 11%, 20%)' }}>{notes.trim().split(/\s+/).length}</span></span>
                  </div>
                  <span className="text-sm font-semibold px-3 py-1 rounded-full" style={{ 
                    backgroundColor: 'hsl(208, 50%, 85%)', 
                    color: 'hsl(208, 61%, 50%)' 
                  }}>
                    Excellent
                  </span>
                </div>
              </div>
            )}

            <button
              type="submit"
              className={`w-full py-4 md:py-3 rounded-lg font-semibold text-white transition-all duration-300 text-lg tracking-wide disabled:opacity-60 shadow-glow hover:scale-105 active:scale-95 min-h-[56px] md:min-h-[48px] ${
                notes.trim() ? 'animate-pulse' : ''
              }`}
              style={{
                backgroundColor: 'hsl(28, 80%, 58%)'
              }}
              disabled={loading || !notes.trim()}
            >
              {loading ? "Generating Brief..." : (pdfUrl ? "Regenerate Brief" : "Generate Brief")}
            </button>
              </form>
            </>
          )}

          {/* Step 3: Results */}
          {currentStep === 3 && pdfUrl && (
            <div className="w-full max-w-2xl animate-slideDownSmooth">
              <div className="rounded-2xl p-8 mb-6 border-2 shadow-strong" style={{ 
                background: 'linear-gradient(to right, hsl(28, 65%, 90%), hsl(208, 50%, 85%))',
                borderColor: 'hsl(28, 80%, 58%)'
              }}>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-2xl gradient-accent flex items-center justify-center">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xl font-bold mb-2" style={{ color: 'hsl(210, 11%, 20%)' }}>Your Design Brief is Ready!</p>
                    <p className="text-base" style={{ color: 'hsl(210, 6%, 56%)' }}>Preview or download your professionally formatted brief</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 md:gap-4 mb-6">
                <button
                  type="button"
                  onClick={downloadPdf}
                  className="w-full md:w-1/2 px-6 py-4 md:py-3 rounded-lg font-semibold text-white transition-all duration-300 text-base tracking-wide shadow-glow hover:scale-105 active:scale-95 min-h-[56px] md:min-h-[48px]"
                  style={{ 
                    backgroundColor: 'hsl(28, 80%, 58%)'
                  }}
                >
                  Download PDF
                </button>
                <button
                  id="preview-button"
                  type="button"
                  onClick={(e) => {
                    setButtonRect(e.currentTarget.getBoundingClientRect());
                  }}
                  className="w-full md:w-1/2 px-6 py-4 md:py-3 rounded-lg font-semibold bg-white border transition-all duration-300 text-base tracking-wide shadow-soft hover:shadow-strong hover:scale-105 active:scale-95 min-h-[56px] md:min-h-[48px]"
                  style={{ 
                    color: 'hsl(208, 56%, 24%)',
                    borderColor: 'hsl(210, 11%, 90%)'
                  }}
                >
                  Preview PDF
                </button>
              </div>
            </div>
          )}
          
          {/* Loading State - visible during generation */}
          {loading && currentStep === 3 && (
            <div className="w-full max-w-2xl flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 mb-6" style={{
                border: '4px solid rgba(255, 255, 255, 0.2)',
                borderTop: '4px solid hsl(28, 80%, 58%)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p className="text-xl font-bold text-white drop-shadow-lg mb-3">Generating your design brief...</p>
              
              {/* Progress stages */}
              <div className="space-y-2">
                <div className="flex items-center gap-3 transition-opacity duration-300" style={{ opacity: loadingStage >= 0 ? 1 : 0.4 }}>
                  <div className={`w-2 h-2 rounded-full transition-all duration-300 ${loadingStage >= 1 ? 'bg-green-400' : 'bg-white/40'}`}></div>
                  <p className="text-sm text-white/90 drop-shadow-lg">Reading content...</p>
                </div>
                <div className="flex items-center gap-3 transition-opacity duration-300" style={{ opacity: loadingStage >= 1 ? 1 : 0.4 }}>
                  <div className={`w-2 h-2 rounded-full transition-all duration-300 ${loadingStage >= 2 ? 'bg-green-400' : 'bg-white/40'}`}></div>
                  <p className="text-sm text-white/90 drop-shadow-lg">Analyzing structure...</p>
                </div>
                <div className="flex items-center gap-3 transition-opacity duration-300" style={{ opacity: loadingStage >= 2 ? 1 : 0.4 }}>
                  <div className={`w-2 h-2 rounded-full transition-all duration-300 ${loadingStage >= 3 ? 'bg-green-400' : 'bg-white/40'}`}></div>
                  <p className="text-sm text-white/90 drop-shadow-lg">Formatting brief...</p>
                </div>
              </div>
              
              <p className="text-xs text-white/70 mt-6 drop-shadow-lg">Usually takes 5-10 seconds</p>
            </div>
          )}
        </main>

        {/* Footer */}
        <footer className="backdrop-blur-sm border-t border-white/20 py-4 text-center text-white/80 text-sm font-medium">
          &copy; {new Date().getFullYear()} Archibrief. All rights reserved.
        </footer>
      </div>

      {/* PDF Preview Modal */}
      {modalState !== 'closed' && (
        <PdfPreviewModal
          pdfUrl={pdfUrl}
          pdfFilename={pdfFilename}
          modalState={modalState}
          setModalState={setModalState}
          buttonRect={buttonRect}
        />
      )}
    </>
  );
}

// PDF Preview Modal Component (same as main App.js)
const PdfPreviewModal = ({ pdfUrl, pdfFilename, modalState, setModalState, buttonRect }) => {
  const [showContent, setShowContent] = useState(false);
  const [currentStyle, setCurrentStyle] = useState({});
  const [dimensions, setDimensions] = useState({
    finalWidth: 0,
    finalHeight: 0,
    finalLeft: 0,
    finalTop: 0,
  });

  useEffect(() => {
    if (modalState === 'opening' || modalState === 'open') {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [modalState]);

  useEffect(() => {
    const calculateDimensions = () => {
      const finalWidth = Math.min(window.innerWidth * 0.9, 800);
      const finalHeight = window.innerHeight * 0.9;
      const finalLeft = (window.innerWidth / 2) - (finalWidth / 2);
      const finalTop = window.innerHeight * 0.05;
      setDimensions({ finalWidth, finalHeight, finalLeft, finalTop });
    };

    calculateDimensions();
    window.addEventListener('resize', calculateDimensions);
    return () => window.removeEventListener('resize', calculateDimensions);
  }, []);

  const modalBaseStyle = {
    transition: 'transform 400ms cubic-bezier(0.4, 0, 0.2, 1), opacity 400ms, border-radius 400ms',
    position: 'fixed',
    backgroundColor: '#E5E7EB',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    transformOrigin: '0 0',
    width: `${dimensions.finalWidth}px`,
    height: `${dimensions.finalHeight}px`,
    top: `${dimensions.finalTop}px`,
    left: `${dimensions.finalLeft}px`,
    borderRadius: modalState === 'preparing' ? '0.5rem' : '0.75rem',
  };

  const scaleX = buttonRect.width / dimensions.finalWidth;
  const scaleY = buttonRect.height / dimensions.finalHeight;
  
  const translateX = buttonRect.left - dimensions.finalLeft;
  const translateY = buttonRect.top - dimensions.finalTop;

  const initialTransform = `translate(${translateX}px, ${translateY}px) scale(${scaleX}, ${scaleY})`;
  const openTransform = 'translate(0, 0) scale(1)';

  useEffect(() => {
    if (modalState === 'preparing') {
      setCurrentStyle({ transform: initialTransform, opacity: 1 });
      requestAnimationFrame(() => {
        setModalState('opening');
      });
    }

    if (modalState === 'opening') {
      setCurrentStyle({ transform: openTransform, opacity: 1 });
    }

    if (modalState === 'closing') {
      setShowContent(false);
      setCurrentStyle({ transform: initialTransform, opacity: 0 });
    }
  }, [modalState, initialTransform, openTransform, setModalState]);

  const handleTransitionEnd = () => {
    if (modalState === 'opening') {
      setModalState('open');
      setShowContent(true);
    }
    if (modalState === 'closing') {
      setModalState('closed');
    }
  };
  
  return (
    <div 
      className={`fixed inset-0 z-50 transition-opacity duration-400 ${modalState === 'opening' || modalState === 'open' ? 'bg-black/70 backdrop-blur-sm' : 'bg-transparent'}`}
      onClick={() => setModalState('closing')}
    >
      <div
        style={{ ...modalBaseStyle, ...currentStyle }}
        onTransitionEnd={handleTransitionEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <div 
          className={`w-full h-full flex flex-col transition-opacity duration-200 delay-100 rounded-xl overflow-hidden ${showContent ? 'opacity-100' : 'opacity-0'}`}
        >
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-800">PDF Preview</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = pdfUrl;
                  link.download = pdfFilename;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="px-6 py-2 rounded-lg font-semibold text-gray-800 bg-white/80 backdrop-blur-sm border border-gray-200 shadow hover:shadow-md transition text-base tracking-wide"
              >
                Download
              </button>
              <button 
                onClick={() => setModalState('closing')} 
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition"
              >
                ×
              </button>
            </div>
          </div>
          <div className="flex-1 p-4 overflow-hidden">
            <div className="w-full h-full rounded-lg overflow-hidden shadow-inner">
              <iframe 
                src={`${pdfUrl}#view=Fit&navpanes=0&toolbar=0&statusbar=0&messages=0`}
                title="PDF Preview" 
                className="w-full h-full bg-white" 
                frameBorder="0"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

